import { credentialsFor } from "../../../../common/utils.es6";
import { authedFetch } from "../../../../common/http_service";

// Library files live on another origin (cloud / asset gateway), where the
// browser ignores <a download> and window.open just navigates to the file.
// Fetch it as a blob and save from a same-origin object URL instead, so the
// click downloads in place. Falls back to opening the file only if the fetch
// itself fails (e.g. a host without CORS).
export const fileNameFromUrl = (url) => {
  try {
    const last = new URL(url, window.location.href).pathname.split("/").pop();
    return decodeURIComponent(last || "") || "download";
  } catch (e) {
    return "download";
  }
};

/** `name` with its audio extension (or none) replaced by `.mp3`. */
export const mp3FileName = (name) => `${(name || "").replace(/\.(mp3|wav)$/i, "") || "audio"}.mp3`;

const MP3_RETRY_DELAY_MS = 2000;
const MP3_MAX_ATTEMPTS = 30;

// mw `POST studio-files/audio/mp3` streams the file as MP3: a WAV is converted
// once on the worker (44.1 kHz mono 128 kbps) and cached next to it; an MP3
// comes back as-is. 202 = still converting, asked again. Anything else (not
// our file, conversion failed) → null and the file is saved as it is.
async function fetchMp3(url) {
  const endpoint = `${process.env.MW_API_URL || ""}api/studio-files/audio/mp3`;
  for (let attempt = 0; attempt < MP3_MAX_ATTEMPTS; attempt++) {
    let response;
    try {
      response = await authedFetch(
        endpoint,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        },
        { quiet: true }
      );
    } catch (e) {
      return null;
    }
    if (response.status === 200) return response.blob();
    if (response.status !== 202) return null;
    await new Promise((resolve) => setTimeout(resolve, MP3_RETRY_DELAY_MS));
  }
  return null;
}

function saveBlob(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * `audioAsMp3`: audio from the library is downloaded as MP3 whatever provider
 * voiced it (see fetchMp3); falls back to the file itself.
 */
export async function downloadFile(url, name, { audioAsMp3 = false } = {}) {
  if (!url) return;
  const fromUrl = fileNameFromUrl(url);
  // Display names can drop the extension; borrow it from the URL then.
  const ext = /\.[a-z0-9]{1,5}$/i.exec(fromUrl);
  const fileName = name ? (/\.[a-z0-9]{1,5}$/i.test(name) || !ext ? name : name + ext[0]) : fromUrl;

  if (audioAsMp3) {
    const mp3 = await fetchMp3(url);
    if (mp3) {
      saveBlob(mp3, mp3FileName(fileName));
      return;
    }
  }

  try {
    const response = await fetch(url, credentialsFor(url));
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    saveBlob(await response.blob(), fileName);
  } catch (e) {
    console.warn("Download failed, opening the file instead:", e);
    window.open(url, "_blank");
  }
}
